import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import clsx from "clsx";
import type { ReactNode } from "react";

const DEFAULT_MAX_VISIBLE_PAGES = 5;

export const getVisiblePaginationPages = (
  currentPage: number,
  totalPages: number,
  maxVisiblePages: number = DEFAULT_MAX_VISIBLE_PAGES,
): number[] => {
  const safeTotalPages = Math.max(1, Math.trunc(totalPages));
  const safeMaxVisiblePages = Math.max(1, Math.trunc(maxVisiblePages));
  const safeCurrentPage = Math.min(
    safeTotalPages,
    Math.max(1, Math.trunc(currentPage)),
  );

  if (safeTotalPages <= safeMaxVisiblePages) {
    return Array.from({ length: safeTotalPages }, (_, index) => index + 1);
  }

  const pagesBeforeCurrent = Math.floor(safeMaxVisiblePages / 2);
  let startPage = Math.max(1, safeCurrentPage - pagesBeforeCurrent);
  let endPage = startPage + safeMaxVisiblePages - 1;

  if (endPage > safeTotalPages) {
    endPage = safeTotalPages;
    startPage = endPage - safeMaxVisiblePages + 1;
  }

  return Array.from(
    { length: endPage - startPage + 1 },
    (_, index) => startPage + index,
  );
};

interface PaginationFooterProps {
  ariaLabel: string;
  className?: string;
  currentPage: number;
  getPageLabel: (page: number) => string;
  hasNextPage: boolean;
  nextPageLabel: string;
  onPageChange: (page: number) => void;
  pageNumbers: number[];
  previousPageLabel: string;
  summary?: ReactNode;
}

const PaginationFooter = ({
  ariaLabel,
  className,
  currentPage,
  getPageLabel,
  hasNextPage,
  nextPageLabel,
  onPageChange,
  pageNumbers,
  previousPageLabel,
  summary,
}: PaginationFooterProps) => (
  <nav
    aria-label={ariaLabel}
    className={clsx(
      "flex items-center justify-between gap-4 border-t border-[#ECEFF5] bg-[#FCFCFC] px-6 py-4",
      className,
    )}
  >
    {summary ? (
      <span className="text-sm font-normal text-[#666F8D]">{summary}</span>
    ) : null}
    <div className={clsx("flex items-center gap-2", !summary && "ml-auto")}>
      <button
        aria-label={previousPageLabel}
        className="rounded-lg p-2 text-[#8A93B2] transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20"
        disabled={currentPage <= 1}
        onClick={() => onPageChange(currentPage - 1)}
        type="button"
      >
        <CaretLeft size={16} weight="regular" />
      </button>
      <div className="flex items-center gap-1">
        {pageNumbers.map((page) => (
          <button
            aria-current={currentPage === page ? "page" : undefined}
            aria-label={getPageLabel(page)}
            className={clsx(
              "h-8 w-8 rounded-lg text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20",
              currentPage === page
                ? "bg-[#1A162F] text-white hover:bg-primary/90"
                : "text-[#666F8D] hover:bg-[#F4F5F8] hover:text-[#1A162F]",
            )}
            key={page}
            onClick={() => onPageChange(page)}
            type="button"
          >
            {page}
          </button>
        ))}
      </div>
      <button
        aria-label={nextPageLabel}
        className="rounded-lg p-2 text-[#8A93B2] transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F] disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20"
        disabled={!hasNextPage}
        onClick={() => onPageChange(currentPage + 1)}
        type="button"
      >
        <CaretRight size={16} weight="regular" />
      </button>
    </div>
  </nav>
);

export default PaginationFooter;
